import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeftIcon, RefreshIcon, SparklesIcon } from "@heroicons/react/outline";
import BetaLabel from "./BetaLabel";

interface Props {
  phase: "generating" | "ready" | "actioning";
  rationale?: string;
  onBack: () => void;
  onApply: () => void;
  onRevert: () => void;
}

function AiSuggestionSidebar(props: Props) {
  const { phase, rationale, onBack, onApply, onRevert } = props;
  const { t } = useTranslation();

  const isActioning = phase === "actioning";
  const isGenerating = phase === "generating";
  const showApply = phase === "ready" || phase === "actioning";

  const [rationaleRevealed, setRationaleRevealed] = useState(false);

  useEffect(() => {
    if (isGenerating) {
      setRationaleRevealed(false);
      return;
    }
    const id = requestAnimationFrame(() => setRationaleRevealed(true));
    return () => cancelAnimationFrame(id);
  }, [isGenerating, phase]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col pt-2 pb-6 shrink-0 border-b border-gray-200">
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs font-semibold font-gilroy text-gray-600 hover:text-gray-800 transition-colors"
            onClick={onBack}
          >
            <ArrowLeftIcon className="w-3.5 h-3.5" />
            {t("Back")}
          </button>
          <div className="w-fit inline-flex items-center gap-1 text-xs font-semibold font-gilroy portant-ai-gradient-text">
            {t("Portant AI")}
            <SparklesIcon className="w-3.5 h-3.5 text-[#1875FB]" />
            <BetaLabel inline className="ml-0" />
          </div>
        </div>
        <div className="mt-4 text-2xl font-gilroy font-semibold text-black leading-tight break-words text-balance">
          {t("Suggested Email Update")}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto pt-4 pb-3 flex flex-col gap-2">
        {!isGenerating &&
          <div className="text-sm font-gilroy font-semibold text-black">{t("Rationale")}</div>
        }

        {isGenerating &&
          <div className="flex flex-col items-center gap-3 py-8 text-sm text-gray-500">
            <RefreshIcon className="w-6 h-6 animate-spin text-[#1875FB]" />
            <p className="text-center text-balance">{t("Writing personalized email… this can take a few seconds.")}</p>
          </div>
        }

        {!isGenerating && !!rationale &&
          <p className={`text-sm leading-relaxed italic text-gray-600 suggestion-content-in-delayed${rationaleRevealed ? " is-revealed" : ""}`}>
            {rationale}
          </p>
        }

        {!isGenerating && !rationale &&
          <p className={`text-sm text-gray-500 suggestion-content-in-delayed${rationaleRevealed ? " is-revealed" : ""}`}>
            {t("Review the suggested changes in the email preview.")}
          </p>
        }
      </div>

      {(isGenerating || showApply) &&
        <div className="shrink-0 pt-4 pb-3 flex items-center gap-3 border-t border-gray-200">
          <button
            type="button"
            className={`${showApply ? "flex-1" : "w-full"} h-10 bg-white hover:bg-gray-50 text-gray-700 font-gilroy font-semibold text-sm rounded-md border border-gray-200 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed`}
            onClick={onRevert}
            disabled={isActioning}
          >
            {isGenerating ? t("Cancel") : t("Revert")}
          </button>
          {showApply &&
            <button
              type="button"
              className="flex-1 h-10 bg-blue hover:opacity-90 text-white font-gilroy font-semibold text-sm rounded-md shadow-[inset_0_0_0_1px_rgba(0,0,0,0.1)] transition-opacity duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
              onClick={onApply}
              disabled={isActioning}
            >
              {isActioning && <RefreshIcon className="w-4 h-4 animate-spin" />}
              {isActioning ? t("Applying") + "…" : t("Apply")}
            </button>
          }
        </div>
      }
    </div>
  );
}

export default AiSuggestionSidebar;
